import React, { useState, useEffect, useRef, Suspense, lazy, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { usePrimaryColor } from '../hooks/usePrimaryColor';
import SEO from '../components/SEO';
import ScrollToTop from '../components/ScrollToTop';
import BreadcrumbNav from '../components/seo/BreadcrumbNav';
import ErrorBoundary from '../components/ErrorBoundary';
import PreLoader from '../components/PreLoader';
import { FaEnvelope, FaPhone, FaWhatsapp, FaComments } from 'react-icons/fa';
import { createContactLead } from '../utils/createContactLead';

const InteractiveMap = lazy(() => import('../components/InteractiveMap'));

function MapSectionPlaceholder() {
  return (
    <div
      className="w-full h-[400px] md:h-[500px] rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center"
      aria-busy="true"
      aria-label="Carregando mapa"
    >
      <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
    </div>
  );
}

// Função auxiliar para formatar horários
const formatHours = (hours) => {
  if (!hours) return 'Não informado';
  if (hours.closed) return 'Fechado';
  if (hours.from && hours.to) {
    // Converte de HH:mm para formato mais legível (ex: 08:00 -> 8h)
    const formatTime = (time) => {
      const [hour, minute] = time.split(':');
      return minute === '00' ? `${parseInt(hour)}h` : `${parseInt(hour)}h${minute}`;
    };
    return `${formatTime(hours.from)} às ${formatTime(hours.to)}`;
  }
  // Se for string (formato antigo), retorna como está
  if (typeof hours === 'string') return hours;
  // Se for objeto mas não tem from/to, retorna string padrão
  return 'Horário não definido';
};

// Função para formatar endereço estruturado
const formatStructuredAddress = (addressStructured) => {
  if (!addressStructured) return 'Não informado';
  
  const { logradouro, numero, complemento, bairro, localidade, uf } = addressStructured;
  
  let address = '';
  if (logradouro) address += logradouro;
  if (numero) address += `, ${numero}`;
  if (complemento) address += ` - ${complemento}`;
  if (bairro) address += `, ${bairro}`;
  if (localidade) address += `, ${localidade}`;
  if (uf) address += ` - ${uf}`;
  
  return address || 'Não informado';
};

// Função para formatar telefone no padrão brasileiro
const formatPhone = (value) => {
  // Remove todos os caracteres não numéricos
  const numbers = value.replace(/\D/g, '');
  
  // Limita a 11 dígitos (DDD + 9 dígitos)
  const limitedNumbers = numbers.slice(0, 11);
  
  // Aplica a formatação baseada no tamanho
  if (limitedNumbers.length <= 2) {
    return limitedNumbers;
  } else if (limitedNumbers.length <= 7) {
    return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2)}`;
  } else if (limitedNumbers.length <= 10) {
    return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2, 6)}-${limitedNumbers.slice(6)}`;
  } else {
    return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
  }
};

function Contact() {
  const companySettings = useCompanySettings();
  const { hash } = useLocation();
  const { 
    companyName,
    contactAddress,
    fullAddress,
    primaryColor,
    settingsReady,
  } = companySettings;

  const visibleBranches =
    companySettings.contactBranchesEnabled === true
      ? companySettings.branches || []
      : [];
  const mapCompanySettings = {
    ...companySettings,
    branches: visibleBranches,
  };
  
  usePrimaryColor();

  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    interesse: '',
    orcamento: '',
    mensagem: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [interesseError, setInteresseError] = useState('');
  const [isInteresseOpen, setIsInteresseOpen] = useState(false);
  const [isOrcamentoOpen, setIsOrcamentoOpen] = useState(false);
  const interesseRef = useRef(null);
  const orcamentoRef = useRef(null);

  // Estados para animações
  const [, setIsLoaded] = useState(false);
  const [isPreLoading, setIsPreLoading] = useState(() => !settingsReady);
  const [mapShouldLoad, setMapShouldLoad] = useState(false);
  const [visibleElements, setVisibleElements] = useState({
    hero: false,
    units: false,
    contact: false,
    map: false
  });

  // Refs para os elementos que serão observados
  const heroRef = useRef(null);
  const unitsRef = useRef(null);
  const contactRef = useRef(null);
  const mapRef = useRef(null);

  // Estado para controlar qual marcador está selecionado no mapa
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);

  // Função para scroll até o formulário
  const scrollToForm = () => {
    const formSection = document.getElementById('contact-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Deep-link /contato#contact-form (ex.: botão CTA da home)
  useEffect(() => {
    if (isPreLoading) return undefined;
    if (hash !== '#contact-form') return undefined;

    const run = () => {
      const formSection = document.getElementById('contact-form');
      if (formSection) {
        formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const t1 = window.setTimeout(run, 80);
    const t2 = window.setTimeout(run, 400);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [hash, isPreLoading]);

  // Função para scroll até o mapa
  const scrollToMap = () => {
    const mapSection = document.getElementById('interactive-map');
    if (mapSection) {
      const elementPosition = mapSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - (window.innerHeight * 0.12);
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Função para selecionar/deselecionar um marcador no mapa
  const handleMarkerSelect = (markerId) => {
    try {
      setMapShouldLoad(true);
      if (selectedMarkerId === markerId) {
        // Se o mesmo marcador for clicado, deseleciona
        setSelectedMarkerId(null);
      } else {
        // Seleciona o novo marcador
        setSelectedMarkerId(markerId);
      }
      
      // Fazer scroll suave até o mapa
      scrollToMap();
    } catch (error) {
      console.error('Erro ao selecionar marcador:', error);
    }
  };

  // Leaflet só entra no bundle quando a seção do mapa se aproxima do viewport
  useEffect(() => {
    const el = mapRef.current;
    if (!el || mapShouldLoad) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMapShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '240px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mapShouldLoad]);

  // Fechar dropdowns quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (interesseRef.current && !interesseRef.current.contains(event.target)) {
        setIsInteresseOpen(false);
      }
      if (orcamentoRef.current && !orcamentoRef.current.contains(event.target)) {
        setIsOrcamentoOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePreLoadingComplete = useCallback(() => {
    setIsPreLoading(false);
  }, []);

  useEffect(() => {
    if (settingsReady) {
      setIsPreLoading(false);
    }
  }, [settingsReady]);

  useEffect(() => {
    // Só inicia as animações após o pre-loading terminar
    if (!isPreLoading) {
      setIsLoaded(true);
      
      // Configuração do Intersection Observer
      const observerOptions = {
        threshold: 0.1, // Elemento deve estar 10% visível
        rootMargin: '0px 0px -50px 0px' // Margem para ativar antes do elemento estar completamente visível
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute('data-animate');
            if (elementId) {
              setVisibleElements(prev => ({ ...prev, [elementId]: true }));
              // Para de observar o elemento após animar
              observer.unobserve(entry.target);
            }
          }
        });
      }, observerOptions);

      // Observar todos os elementos com data-animate
      const elementsToObserve = [
        heroRef.current,
        unitsRef.current,
        contactRef.current,
        mapRef.current
      ].filter(Boolean);

      elementsToObserve.forEach(element => {
        if (element) {
          observer.observe(element);
        }
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [isPreLoading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const maxLengths = { nome: 35, email: 50, mensagem: 150 };
    const max = maxLengths[name];
    const nextValue = max != null ? value.slice(0, max) : value;
    
    // Aplica formatação específica para telefone
    if (name === 'telefone') {
      const formatted = formatPhone(nextValue);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: nextValue
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);

    if (!formData.interesse) {
      setInteresseError('Selecione um interesse.');
      return;
    }

    setInteresseError('');
    setIsSubmitting(true);

    try {
      await createContactLead({
        name: formData.nome,
        phone: formData.telefone,
        email: formData.email,
        message: formData.mensagem,
        interesse: formData.interesse,
        orcamento: formData.orcamento
      });
      setSubmitSuccess(true);
      setFormData({
        nome: '',
        telefone: '',
        email: '',
        interesse: '',
        orcamento: '',
        mensagem: ''
      });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Erro ao enviar formulário de contato:', error);
      setSubmitError(
        error?.message?.includes('Muitas tentativas')
          ? error.message
          : 'Não foi possível enviar sua mensagem. Tente novamente em instantes.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Funções para lidar com seleção personalizada
  const handleInteresseSelect = (value) => {
    if (!value) return;
    setFormData(prev => ({ ...prev, interesse: value }));
    setInteresseError('');
    setIsInteresseOpen(false);
  };

  const handleOrcamentoSelect = (value, label) => {
    setFormData(prev => ({ ...prev, orcamento: value }));
    setIsOrcamentoOpen(false);
  };

  const getSelectedInteresseLabel = () => {
    const option = interesseOptions.find(opt => opt.value === formData.interesse);
    return option ? option.label : 'Selecione seu interesse';
  };

  const getSelectedOrcamentoLabel = () => {
    const option = orcamentoOptions.find(opt => opt.value === formData.orcamento);
    return option ? option.label : 'Faixa de preço';
  };

  const interesseOptions = [
    { value: 'compra', label: 'Compra' },
    { value: 'venda', label: 'Venda' },
    { value: 'aluguel', label: 'Aluguel' },
    { value: 'locacao', label: 'Locação' },
    { value: 'avaliacao', label: 'Avaliação' },
    { value: 'outros', label: 'Outros' }
  ];

  const orcamentoOptions = [
    { value: '', label: 'Faixa de preço' },
    { value: 'ate-100k', label: 'Até R$ 100.000' },
    { value: '100k-200k', label: 'R$ 100.000 - R$ 200.000' },
    { value: '200k-300k', label: 'R$ 200.000 - R$ 300.000' },
    { value: '300k-500k', label: 'R$ 300.000 - R$ 500.000' },
    { value: '500k-1m', label: 'R$ 500.000 - R$ 1.000.000' },
    { value: 'acima-1m', label: 'Acima de R$ 1.000.000' }
  ];

  const contactBreadcrumbs = [
    { name: 'Início', path: '/' },
    { name: 'Contato', current: true },
  ];

  return (
    <>
      {/* PreLoader Component */}
      <PreLoader 
        isLoading={isPreLoading} 
        onLoadingComplete={handlePreLoadingComplete}
      />
      
      <SEO 
        title={`Contato - ${companyName || 'Imobiliária'}`}
        description={
          companyName
            ? `Entre em contato com ${companyName}${fullAddress || contactAddress ? ` — ${fullAddress || contactAddress}` : ''}. Atendimento para compra, venda e aluguel de imóveis.`
            : 'Entre em contato conosco. Estamos prontos para ajudar você a encontrar o imóvel dos seus sonhos.'
        }
        keywords="contato, imobiliária, atendimento, vendas, aluguel, locação"
        canonicalUrl="/contato"
        includeSiteSchemas
      />
      <ScrollToTop />
      
      <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        {/* Hero Section */}
        <section 
          ref={heroRef}
          data-animate="hero"
          className={`relative bg-white text-gray-900 overflow-hidden transition-all duration-1000 ${visibleElements.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Breadcrumbs */}
          <div className="relative z-40 pt-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-8">
                <BreadcrumbNav items={contactBreadcrumbs} />
              </div>
            </div>
          </div>

          {/* Floating Contact Icons */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
              <FaPhone className="w-10 h-10 text-gray-500" />
            </div>
            <div className="absolute top-40 right-20 w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>
              <FaEnvelope className="w-8 h-8 text-gray-500" />
            </div>
            <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '2s', animationDuration: '5s' }}>
              <FaComments className="w-6 h-6 text-gray-500" />
            </div>
            <div className="absolute bottom-10 right-1/3 w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center animate-bounce" style={{ animationDelay: '1.5s', animationDuration: '3.5s' }}>
              <FaWhatsapp className="w-12 h-12 text-gray-500" />
            </div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="animate-fade-in-up">
                  <h1 className="text-4xl lg:text-5xl font-bold leading-tight text-gray-900 typewriter">
                    {companySettings.contactPageTitle || 'Entre em Contato'}
                  </h1>
                </div>
                
                <div 
                  className="text-lg leading-relaxed text-gray-600 space-y-4 animate-fade-in-up opacity-0"
                  style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
                  dangerouslySetInnerHTML={{ __html: companySettings.contactPageDescription || 'Estamos prontos para ajudar você a encontrar o imóvel dos seus sonhos' }}
                />
                
                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
                  <button
                    onClick={scrollToForm}
                    className="group inline-flex items-center justify-center px-8 py-4 border-2 font-semibold rounded-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-lg hover:shadow-xl"
                    style={{
                      borderColor: 'var(--primary-color, #3B82F6)',
                      backgroundColor: 'var(--primary-color, #3B82F6)',
                      color: 'var(--secondary-color, #10B981)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--secondary-color, #10B981)';
                      e.target.style.color = 'var(--primary-color, #3B82F6)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-color, #3B82F6)';
                      e.target.style.color = 'var(--secondary-color, #10B981)';
                    }}
                  >
                    <span className="relative z-10">Fale com Especialista</span>
                    <svg className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                  <button
                    onClick={scrollToMap}
                    className="group inline-flex items-center justify-center px-8 py-4 border-2 font-semibold rounded-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-lg hover:shadow-xl"
                    style={{
                      borderColor: 'var(--primary-color, #3B82F6)',
                      backgroundColor: 'var(--secondary-color, #10B981)',
                      color: 'var(--primary-color, #3B82F6)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--primary-color, #3B82F6)';
                      e.target.style.color = 'var(--secondary-color, #10B981)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--secondary-color, #10B981)';
                      e.target.style.color = 'var(--primary-color, #3B82F6)';
                    }}
                  >
                    <span className="relative z-10">Ver Localização</span>
                  </button>
                </div>
              </div>
              
              <div className="relative animate-fade-in-up opacity-0" style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-700">
                  <img 
                    src={companySettings.contactHeroImage || require('../back.jpg')} 
                    alt="Entre em Contato"
                    className="w-full h-96 lg:h-[500px] object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>
                
                {/* Floating Contact Cards */}
                <div className="absolute bottom-2 left-2 lg:-bottom-6 lg:-left-6 bg-white rounded-lg p-2 lg:p-4 shadow-xl transform hover:scale-110 hover:-rotate-2 transition-all duration-500 animate-float">
                  <div className="flex items-center space-x-2 lg:space-x-4">
                    <div className="p-2 lg:p-3 bg-blue-100 rounded-lg">
                      <FaPhone className="w-4 h-4 lg:w-6 lg:h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-base lg:text-2xl font-bold text-gray-900 animate-count-up">{companySettings.contactPhone || '(11) 3456-7890'}</p>
                      <p className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">Ligue Agora</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute top-2 right-2 lg:-top-6 lg:-right-6 bg-white rounded-lg p-2 lg:p-4 shadow-xl transform hover:scale-110 hover:rotate-2 transition-all duration-500 animate-float-delayed">
                  <div className="flex items-center space-x-2 lg:space-x-4">
                    <div className="p-2 lg:p-3 bg-green-100 rounded-lg animate-pulse">
                      <FaWhatsapp className="w-4 h-4 lg:w-6 lg:h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-base lg:text-2xl font-bold text-gray-900 animate-count-up-delayed">WhatsApp</p>
                      <p className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">Chat Rápido</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>




        {/* Nossas Unidades Section */}
        <div 
          ref={unitsRef}
          data-animate="units"
          className={`bg-gray-50 py-16 transition-all duration-1000 delay-400 ${visibleElements.units ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Nossas Unidades
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Localizações estratégicas para melhor atendê-lo
              </p>
            </div>
            
            {/* Unidades */}
            <div className="flex flex-wrap justify-center gap-8">
              {/* Matriz */}
              <div 
                className={`w-full md:w-[calc((100%-4rem)/3)] bg-white p-6 rounded-lg shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  selectedMarkerId === 'matriz' ? 'border-2 border-black' : 'border-2 border-transparent'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMarkerSelect('matriz');
                }}
              >
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  {companySettings.contactName || 'Matriz - Centro'}
                </h4>
                <div className="space-y-3 text-gray-600">
                  <p><strong>Endereço:</strong> {formatStructuredAddress(companySettings.contactAddressStructured) || companySettings.contactAddress || 'Rua das Flores, 123 - Centro'}</p>
                  <p><strong>Telefone:</strong> {companySettings.contactPhone || '(11) 3456-7890'}</p>
                  <p><strong>Horário:</strong> Seg-Sex: {formatHours(companySettings.contactHours?.weekdays)}</p>
                </div>
                <div className="mt-4 text-sm text-blue-600 font-medium">
                  Clique para ver no mapa
                </div>
              </div>
              
              {/* Filiais */}
              {visibleBranches.map((branch, index) => (
                <div 
                  key={branch.id} 
                  className={`w-full md:w-[calc((100%-4rem)/3)] bg-white p-6 rounded-lg shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    selectedMarkerId === branch.id ? 'border-2 border-black' : 'border-2 border-transparent'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMarkerSelect(branch.id);
                  }}
                >
                  <h4 className="text-xl font-bold text-gray-900 mb-4">{branch.name}</h4>
                  <div className="space-y-3 text-gray-600">
                    <p><strong>Endereço:</strong> {formatStructuredAddress(branch.addressStructured) || branch.address || 'Não informado'}</p>
                    <p><strong>Telefone:</strong> {branch.phone || 'Não informado'}</p>
                    <p><strong>Horário:</strong> Seg-Sex: {formatHours(branch.hours?.weekdays)}</p>
                  </div>
                  <div className="mt-4 text-sm text-blue-600 font-medium">
                    Clique para ver no mapa
                  </div>
                </div>
              ))}
            </div>
            
            {/* Mapa Interativo */}
            <div className="mt-12">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Mapa Interativo</h3>
                <p className="text-gray-600">
                  Visualize nossas unidades e encontre a mais próxima de você
                </p>
              </div>
              <div 
                id="interactive-map"
                ref={mapRef}
                data-animate="map"
                className={`transition-all duration-1000 delay-800 ${visibleElements.map ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <ErrorBoundary>
                  {mapShouldLoad ? (
                    <Suspense fallback={<MapSectionPlaceholder />}>
                      <InteractiveMap 
                        companySettings={mapCompanySettings} 
                        selectedMarkerId={selectedMarkerId}
                        onMarkerSelect={handleMarkerSelect}
                      />
                    </Suspense>
                  ) : (
                    <MapSectionPlaceholder />
                  )}
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>

        {/* Entre em Contato Section */}
        <div 
          ref={contactRef}
          data-animate="contact"
          className={`bg-white py-16 transition-all duration-1000 delay-600 ${visibleElements.contact ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                     <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Entre em Contato
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Preencha o formulário abaixo e entraremos em contato o mais rápido possível. Estamos prontos para tirar suas dúvidas e oferecer a melhor solução para você.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Informações de Contato */}
              <div className="space-y-6">
                {/* Telefones */}
                <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Telefones</h3>
                  </div>
                                     <div className="space-y-2 text-gray-600">
                     <p><strong>{companySettings.contactName || 'Matriz'}:</strong> {companySettings.contactPhone || '(11) 3456-7890'}</p>
                     {visibleBranches.map((branch) => (
                       <p key={branch.id}><strong>{branch.name}:</strong> {branch.phone || 'Não informado'}</p>
                     ))}
                   </div>
                </div>
                
                {/* E-mails */}
                <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">E-mails</h3>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    <p><strong>{companySettings.contactName || 'Matriz'}:</strong> {companySettings.contactEmail || 'contato@imobiliaria.com.br'}</p>
                    {visibleBranches.map((branch) => (
                      <p key={branch.id}><strong>{branch.name}:</strong> {branch.email || 'Não informado'}</p>
                    ))}
                  </div>
                </div>
                
                {/* Horário de Funcionamento */}
                <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Horário de Funcionamento</h3>
                  </div>
                  <div className="space-y-2 text-gray-600">
                    {/* Horários da Matriz */}
                    <div>
                      <p className="font-semibold text-gray-800 mb-2">{companySettings.contactName || 'Matriz'}:</p>
                      <div className="ml-4 space-y-1">
                        <p><strong>Segunda a Sexta:</strong> {formatHours(companySettings.contactHours?.weekdays)}</p>
                        <p><strong>Sábados:</strong> {formatHours(companySettings.contactHours?.saturdays)}</p>
                        <p><strong>Domingos:</strong> {formatHours(companySettings.contactHours?.sundays)}</p>
                      </div>
                    </div>
                    
                    {/* Horários das Filiais */}
                    {visibleBranches.map(branch => (
                      <div key={`hours-${branch.id}`} className="mt-4 pt-4 border-t border-gray-200">
                        <p className="font-semibold text-gray-800 mb-2">{branch.name}:</p>
                        <div className="ml-4 space-y-1">
                          <p><strong>Segunda a Sexta:</strong> {formatHours(branch.hours?.weekdays)}</p>
                          <p><strong>Sábados:</strong> {formatHours(branch.hours?.saturdays)}</p>
                          <p><strong>Domingos:</strong> {formatHours(branch.hours?.sundays)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Formulário de Contato */}
              <div id="contact-form" className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Envie sua Mensagem</h3>
                
                {submitSuccess && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                    Mensagem enviada com sucesso! Entraremos em contato em breve.
                  </div>
                )}

                {submitError && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                    {submitError}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      required
                      maxLength={35}
                      placeholder="Seu nome completo"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      required
                      placeholder="(11) 99999-9999"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      maxLength={50}
                      placeholder="seu@email.com"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interesse <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={interesseRef}>
                      <button
                        type="button"
                        onClick={() => setIsInteresseOpen(!isInteresseOpen)}
                        aria-required="true"
                        aria-invalid={Boolean(interesseError)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent bg-white text-left flex justify-between items-center transition-colors duration-200 hover:border-gray-400 ${
                          interesseError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        style={{
                          '--tw-ring-color': primaryColor || '#3B82F6'
                        }}
                      >
                        <span className={formData.interesse ? 'text-gray-900' : 'text-gray-500'}>
                          {getSelectedInteresseLabel()}
                        </span>
                        <svg 
                          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isInteresseOpen ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isInteresseOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {interesseOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleInteresseSelect(option.value)}
                              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 ${
                                formData.interesse === option.value 
                                  ? 'text-white' 
                                  : 'text-gray-900'
                              } ${
                                formData.interesse === option.value 
                                  ? '' 
                                  : 'hover:bg-gray-50'
                              }`}
                              style={{
                                backgroundColor: formData.interesse === option.value ? (primaryColor || '#3B82F6') : 'transparent'
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {interesseError && (
                      <p className="mt-2 text-sm text-red-600">{interesseError}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Orçamento
                    </label>
                    <div className="relative" ref={orcamentoRef}>
                      <button
                        type="button"
                        onClick={() => setIsOrcamentoOpen(!isOrcamentoOpen)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent bg-white text-left flex justify-between items-center transition-colors duration-200 hover:border-gray-400"
                        style={{
                          '--tw-ring-color': primaryColor || '#3B82F6'
                        }}
                      >
                        <span className={formData.orcamento ? 'text-gray-900' : 'text-gray-500'}>
                          {getSelectedOrcamentoLabel()}
                        </span>
                        <svg 
                          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOrcamentoOpen ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isOrcamentoOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {orcamentoOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleOrcamentoSelect(option.value, option.label)}
                              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 ${
                                formData.orcamento === option.value 
                                  ? 'text-white' 
                                  : 'text-gray-900'
                              } ${
                                formData.orcamento === option.value 
                                  ? '' 
                                  : 'hover:bg-gray-50'
                              }`}
                              style={{
                                backgroundColor: formData.orcamento === option.value ? (primaryColor || '#3B82F6') : 'transparent'
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                      Mensagem
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      value={formData.mensagem}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={150}
                      placeholder="Conte-nos mais detalhes sobre o que você procura..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <p className="mt-1 text-xs text-gray-500 text-right">
                      {formData.mensagem.length}/150
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Custom CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes floatDelayed {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        
        @keyframes countUp {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes countUpDelayed {
          from {
            opacity: 0;
            transform: scale(0.3) rotate(-10deg);
          }
          to {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: floatDelayed 4s ease-in-out infinite;
          animation-delay: 1s;
        }
        
        .animate-count-up {
          animation: countUp 1s ease-out forwards;
          animation-delay: 0.5s;
        }
        
        .animate-count-up-delayed {
          animation: countUpDelayed 1.2s ease-out forwards;
          animation-delay: 1s;
        }
        
        /* Typewriter effect */
        @keyframes typewriter {
          from {
            clip-path: inset(0 100% 0 0);
          }
          to {
            clip-path: inset(0 0% 0 0);
          }
        }
        
        .typewriter {
          position: relative;
          animation: typewriter 3s ease-in-out 0.5s forwards;
          clip-path: inset(0 100% 0 0);
        }
      `}</style>
    </>
  );
}

export default Contact;
